import React from 'react';

/**
 * The AI "presence" on the call stage — a premium, abstract voice orb (no cartoon face).
 *
 * The sphere is always a living, brand-coloured gradient (indigo → purple → teal) so it looks
 * alive even at rest. The *state* (listening / thinking / speaking / idle) is communicated by the
 * outer glow, the halo rings and the waveform — all of which also react to the candidate's mic
 * level. Placeholder for the Phase-2 avatar video, which renders into the same stage slot.
 */
const STATE = {
  // brand palette: primary #3B5BDB · secondary #20C997 · accent #845EF7
  speaking:  { accent: '#845EF7', rgb: '132,94,247',  label: 'Speaking' },   // AI talking → accent purple
  listening: { accent: '#3B5BDB', rgb: '59,91,219',   label: 'Listening' },  // candidate → primary blue
  thinking:  { accent: '#20C997', rgb: '32,201,151',  label: 'Thinking' },   // processing → secondary teal
  checking:  { accent: '#64748b', rgb: '100,116,139', label: 'Checking delivery' },
  idle:      { accent: '#94a3b8', rgb: '148,163,184', label: 'Ready' },
};

const BAR_WEIGHTS = [0.35, 0.6, 0.85, 1, 0.8, 1, 0.85, 0.6, 0.35];   // centre-weighted equalizer shape

const AiPresenceOrb = ({ state = 'idle', level = 0, compact = false, stateLabel }) => {
  const s = STATE[state] || STATE.idle;
  const active = state !== 'idle';
  const speaking = state === 'speaking';
  const listening = state === 'listening';
  const lvl = Math.min(1, level * 1.6);

  // the core breathes: driven by the candidate's mic while listening, gentle otherwise
  const coreScale = listening ? 1 + Math.min(0.16, level * 0.45) : 1;
  const glowSpread = active ? 40 + lvl * 34 : 26;

  const SIZE = 188;        // sphere diameter
  const BOX = 248;         // stage box (room for rings + glow)

  return (
    <div className="flex flex-col items-center justify-center gap-7">
      <style>{`
        @keyframes orbDrift  { 0%{transform:translate(-14%,-8%) scale(1)} 33%{transform:translate(12%,10%) scale(1.15)} 66%{transform:translate(8%,-12%) scale(.92)} 100%{transform:translate(-14%,-8%) scale(1)} }
        @keyframes orbDrift2 { 0%{transform:translate(16%,12%) scale(1.1)} 50%{transform:translate(-12%,-10%) scale(.9)} 100%{transform:translate(16%,12%) scale(1.1)} }
        @keyframes orbDrift3 { 0%{transform:translate(2%,14%) scale(.95)} 50%{transform:translate(-6%,-14%) scale(1.18)} 100%{transform:translate(2%,14%) scale(.95)} }
        @keyframes orbSpin   { to { transform: rotate(360deg) } }
        @keyframes orbRing   { 0%{transform:scale(.78);opacity:.6} 100%{transform:scale(1.55);opacity:0} }
        @keyframes orbFloat  { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }
        @keyframes orbEq     { 0%,100%{transform:scaleY(.32)} 50%{transform:scaleY(1)} }
        @keyframes orbShimmer{ 0%,100%{opacity:.35} 50%{opacity:.7} }
      `}</style>

      {/* sphere + halo rings + ambient glow */}
      <div className="relative flex items-center justify-center" style={{ width: BOX, height: BOX }}>
        {/* soft ambient glow puddle behind everything */}
        <div
          className="pointer-events-none absolute rounded-full"
          style={{
            width: SIZE * 1.5, height: SIZE * 1.5,
            background: `radial-gradient(circle, rgba(${s.rgb},0.28), transparent 62%)`,
            transition: 'background 400ms ease',
            animation: 'orbShimmer 4s ease-in-out infinite',
          }}
        />

        {/* expanding halo rings (only when active) */}
        {active && [0, 0.7, 1.4].map((d) => (
          <span
            key={d}
            className="pointer-events-none absolute rounded-full"
            style={{
              width: SIZE, height: SIZE,
              border: `1.5px solid rgba(${s.rgb},0.5)`,
              animation: `orbRing ${speaking ? 1.7 : 2.6}s ease-out ${d}s infinite`,
            }}
          />
        ))}

        {/* the orb itself */}
        <div
          className="relative overflow-hidden rounded-full"
          style={{
            width: SIZE, height: SIZE,
            background: '#0b1020',
            boxShadow: `0 0 ${glowSpread}px ${active ? 6 : 3}px rgba(${s.rgb},0.5),
                        inset 0 2px 14px rgba(255,255,255,0.18),
                        inset 0 -18px 36px rgba(0,0,0,0.55)`,
            transition: 'box-shadow 280ms ease',
            animation: state === 'idle' ? 'orbFloat 5s ease-in-out infinite' : undefined,
          }}
        >
          {/* living gradient mesh — three drifting, blurred colour blobs (always brand-coloured) */}
          <div className="absolute inset-0" style={{ filter: 'blur(14px)' }}>
            <span
              className="absolute rounded-full"
              style={{
                width: '78%', height: '78%', left: '4%', top: '2%',
                background: 'radial-gradient(circle, #4c6ef5, transparent 68%)',
                animation: `orbDrift ${speaking ? 6 : 11}s ease-in-out infinite`,
              }}
            />
            <span
              className="absolute rounded-full"
              style={{
                width: '72%', height: '72%', right: '0%', top: '14%',
                background: 'radial-gradient(circle, #9775fa, transparent 66%)',
                animation: `orbDrift2 ${speaking ? 7 : 13}s ease-in-out infinite`,
              }}
            />
            <span
              className="absolute rounded-full"
              style={{
                width: '70%', height: '70%', left: '14%', bottom: '0%',
                background: 'radial-gradient(circle, #20C997, transparent 70%)',
                animation: `orbDrift3 ${speaking ? 8 : 15}s ease-in-out infinite`,
              }}
            />
          </div>

          {/* slow rotating sheen for an iridescent, alive feel */}
          <div
            className="absolute inset-0 rounded-full"
            style={{
              background: 'conic-gradient(from 0deg, transparent, rgba(255,255,255,0.10), transparent 40%)',
              animation: 'orbSpin 9s linear infinite',
              mixBlendMode: 'screen',
            }}
          />

          {/* reactive bright core — scales/intensifies with the candidate's voice */}
          <div
            className="absolute left-1/2 top-1/2 rounded-full"
            style={{
              width: '46%', height: '46%',
              transform: `translate(-50%,-50%) scale(${coreScale.toFixed(3)})`,
              background: `radial-gradient(circle, rgba(255,255,255,${(0.5 + lvl * 0.4).toFixed(2)}), rgba(${s.rgb},0.25) 60%, transparent 75%)`,
              transition: 'transform 90ms linear, background 160ms ease',
              filter: 'blur(2px)',
            }}
          />

          {/* glassy top highlight */}
          <div
            className="pointer-events-none absolute rounded-full"
            style={{
              width: '55%', height: '38%', left: '14%', top: '8%',
              background: 'radial-gradient(circle at 50% 0%, rgba(255,255,255,0.45), transparent 70%)',
              filter: 'blur(2px)',
            }}
          />

          {/* crisp rim light */}
          <div className="pointer-events-none absolute inset-0 rounded-full"
            style={{ boxShadow: 'inset 0 0 1px 1px rgba(255,255,255,0.22)' }} />
        </div>
      </div>

      {/* elegant waveform (hidden in compact PiPs — just the orb shows) */}
      {!compact && (
      <div className="flex items-end gap-[6px]" style={{ height: 40 }} aria-hidden>
        {BAR_WEIGHTS.map((w, i) => {
          const h = listening
            ? 5 + lvl * 34 * w                       // driven by the candidate's mic
            : speaking ? 34 * w : 5 + 5 * w;         // animated while AI talks, low otherwise
          return (
            <span
              key={i}
              className="rounded-full"
              style={{
                width: 4,
                height: h,
                transformOrigin: 'bottom center',
                background: `linear-gradient(${s.accent}, rgba(${s.rgb},0.3))`,
                boxShadow: active ? `0 0 6px rgba(${s.rgb},0.5)` : 'none',
                transition: listening ? 'height 80ms linear' : 'height 200ms ease',
                animation: speaking ? `orbEq ${0.7 + (i % 3) * 0.16}s ease-in-out ${i * 0.06}s infinite` : undefined,
              }}
            />
          );
        })}
      </div>
      )}

      {/* state pill */}
      {!compact && (
      <div className="flex items-center gap-2 rounded-full bg-white px-3.5 py-1.5 text-sm font-medium text-slate-700 shadow-sm ring-1 ring-black/5 backdrop-blur dark:bg-white/10 dark:text-white/90 dark:shadow-none dark:ring-white/10">
        <span className="h-2 w-2 rounded-full" style={{ background: s.accent, boxShadow: `0 0 10px ${s.accent}` }} />
        {stateLabel || s.label}
      </div>
      )}
    </div>
  );
};

export default AiPresenceOrb;
